import React, { useState, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../contexts/PopupContext';
import SubTabs from '../components/layout/SubTabs';
import UpcomingView from '../components/events/UpcomingView';
import HistoryView from '../components/events/HistoryView';
import PendingView from '../components/events/PendingView';
import InvitationsView from '../components/events/InvitationsView';
import './MyEventsPage.css';

const MY_EVENTS_INITIAL_SUBTAB_KEY = 'myEventsInitialSubtab';
const MY_EVENTS_VALID_SUBTABS = new Set(['upcoming', 'pending', 'invitations', 'history']);

function MyEventsPage() {
  const { t } = useTranslation('events');
  const { openPopup } = usePopup();
  const [eventView, setEventView] = useState('upcoming'); // 'upcoming', 'pending', 'invitations' lub 'history'
  const [refreshKey, setRefreshKey] = useState(0);

  useLayoutEffect(() => {
    try {
      const raw = sessionStorage.getItem(MY_EVENTS_INITIAL_SUBTAB_KEY);
      if (raw && MY_EVENTS_VALID_SUBTABS.has(raw)) {
        sessionStorage.removeItem(MY_EVENTS_INITIAL_SUBTAB_KEY);
        setEventView(raw);
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const handleEventCreated = () => {
    setRefreshKey((key) => key + 1);
    setEventView('upcoming');
  };

  const handleSwitchToUpcoming = useCallback(() => {
    setEventView('upcoming');
    setRefreshKey((key) => key + 1);
  }, []);

  const eventTabs = [
    { id: 'upcoming', label: t('events.upcoming.title') },
    { id: 'pending', label: t('events.pending.title') },
    { id: 'invitations', label: t('events.invitations.title') },
    { id: 'history', label: t('events.history.title') },
  ];

  const handleCreateEvent = () => {
    openPopup('create-event', { onSuccess: handleEventCreated }, 0);
  };

  return (
    <div className="my-events-page">
      <div className="page-container">
        <header className="page-header">
          <div className="page-header-top">
            <div>
              <h1>{t('events.title')}</h1>
              <p className="page-subtitle">{t('events.subtitle')}</p>
            </div>
            <button 
              className="create-event-button" 
              onClick={handleCreateEvent}
              aria-label={t('events.createEvent.button')}
            >
              <span className="create-event-button-icon" aria-hidden="true">+</span>
              {t('events.createEvent.button')}
            </button>
          </div>
        </header>

        <SubTabs tabs={eventTabs} activeTab={eventView} setActiveTab={setEventView} />

        <main className="events-content">
          <div hidden={eventView !== 'upcoming'}>
            <UpcomingView
              refreshKey={refreshKey}
              isActive={eventView === 'upcoming'}
            />
          </div>
          <div hidden={eventView !== 'pending'}>
            <PendingView
              refreshKey={refreshKey}
              isActive={eventView === 'pending'}
              onSwitchToUpcoming={handleSwitchToUpcoming}
            />
          </div>
          <div hidden={eventView !== 'invitations'}>
            <InvitationsView
              refreshKey={refreshKey}
              isActive={eventView === 'invitations'}
              onSwitchToUpcoming={handleSwitchToUpcoming}
            />
          </div>
          <div hidden={eventView !== 'history'}>
            <HistoryView refreshKey={refreshKey} isActive={eventView === 'history'} />
          </div>
        </main>
      </div>
    </div>
  );
}

export default MyEventsPage;
